import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import pluralize from '@lib/pluralize'
import LaunchIcon from '@material-ui/icons/Launch'
import LockIcon from '@material-ui/icons/Lock'
import { gtmEvent } from '@lib/gtm-utils'
import { DateTime } from 'luxon'
import JoinWaitlistDialog from './join-waitlist-dialog'
import { useStyles } from './styles'

const RegisterButton = ({ event }) => {
  const {
    title,
    tags,
    startDateTime,
    coachFacility,
    priceInCents,
    coach,
    externalRegistrationUrl,
    isCanceled,
    registrationFull,
    registrationClosed,
    maxAttendance,
    registrationCount,
    slug,
    requiresAccessCode,
    hasWaitlist,
    id,
  } = event
  const classes = useStyles()
  const showWaitlistDialog = registrationFull && hasWaitlist

  const label = (() => {
    if (isCanceled) return 'Event Canceled'
    if (registrationClosed) return 'Registration Closed'
    if (showWaitlistDialog) {
      return 'Join Waitlist'
    }
    if (registrationFull) return 'Registration Full'
    return 'Register'
  })()

  const isRegistrationEnabled = !(isCanceled || registrationClosed || registrationFull)

  const spotsRemaining = maxAttendance != null && maxAttendance - registrationCount

  const shouldDisplaySpotsRemaining = isRegistrationEnabled
    && spotsRemaining && spotsRemaining > 0 && spotsRemaining <= 10

  const eventAttributes = {
    title,
    category: tags,
    start_date: DateTime.fromISO(startDateTime).toFormat('yyyy-LL-dd'),
    start_time: DateTime.fromISO(startDateTime).toFormat('hh:mm a'),
    facility: coachFacility?.name,
    price: priceInCents / 100,
    coach_id: coach?.name,
    coach_name: coach?.id,
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {showWaitlistDialog ? (
        <JoinWaitlistDialog
          trigger={(
            <Button
              className={classes.registerButton}
              variant="contained"
              size="small"
              color="primary"
              id="join-waitlist-button"
              component="span"
              data-gtm-button-name="join-waitlist"
            >
              {label}
            </Button>
          )}
          eventId={id}
          onOpen={() => gtmEvent({ event: 'click-join-waitlist', attributes: { event_name: title } })}
          onSuccess={() => gtmEvent({
            event: 'join-waitlist',
            attributes: eventAttributes,
          })}
        />
      )
        : (
          <Button
            className={classes.registerButton}
            variant="contained"
            size="small"
            color="primary"
            href={externalRegistrationUrl ?? `${process.env.MY_PGA_COM_URL}/things-to-do/events/${slug}/register`}
            disabled={!isRegistrationEnabled}
            startIcon={requiresAccessCode ? <LockIcon /> : null}
            endIcon={externalRegistrationUrl ? <LaunchIcon /> : null}
            target={externalRegistrationUrl ? '_blank' : undefined}
            rel={externalRegistrationUrl ? 'noopener noreferrer' : undefined}
            data-gtm-button-name={`${label}${externalRegistrationUrl ? '-external' : ''}`}
            onClick={() => isRegistrationEnabled && gtmEvent({
              event: 'click-register-for-event',
              attributes: eventAttributes,
            })}
          >
            {label}
          </Button>
        )}
      <Box mt={0.5} display={shouldDisplaySpotsRemaining ? 'block' : 'none'}>
        <Typography variant="caption" color="error" style={{ fontWeight: 700 }}>
          {spotsRemaining} {pluralize(spotsRemaining, 'spot')} remaining
        </Typography>
      </Box>
    </Box>
  )
}

RegisterButton.propTypes = {
  event: PropTypes.shape({
    isCanceled: PropTypes.bool.isRequired,
    registrationClosed: PropTypes.bool.isRequired,
    registrationFull: PropTypes.bool.isRequired,
    registrationCount: PropTypes.number.isRequired,
    maxAttendance: PropTypes.number,
    slug: PropTypes.string.isRequired,
    externalRegistrationUrl: PropTypes.string,
    requiresAccessCode: PropTypes.bool,
    hasWaitlist: PropTypes.bool,
    id: PropTypes.string.isRequired,
  }).isRequired,
}

export default RegisterButton
